/* eslint-disable react-hooks/exhaustive-deps */
import {Communication} from "types/communication"
import styles from "./TopicDetails.module.css"
import {Compose, Message} from "sections/NewCommunication/shared"
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from "react"
import {useQuery} from "@tanstack/react-query"
import {chatServiceV2} from "services"
import {checkPermission, handleErrorChat} from "helpers"
import InfiniteScroll from "react-infinite-scroller"
import {Spin} from "antd"
import ChatWsV2 from "sections/NewCommunication/chatWsV2"
import {useModel} from "hooks"
import {CommunicationContext} from "context/CommunicationContext"
import cx from "classnames"
import {useMessageCompose} from "sections/NewCommunication/hooks"
import {Permissions} from "@edular/permissions"

type Props = {
    topic: Communication.ChannelTopic
    composeClassName?: string
    canWrite?: boolean
}

export function TopicDetails(props: Props) {
    const {topic, composeClassName, canWrite: initialCanWrite = false} = props
    const [comments, setComments] = useState<Communication.Message[]>([])
    const [fragmentMessage, setFragmentMessage] = useState<Communication.FragmentMessage | undefined>()
    const model = useModel()
    const composeRef = useRef<any>()
    const fetchingData = useRef<any>()
    const {setFoundMessageId} = useContext(CommunicationContext)
    const {editMessage, onEditMessage} = useMessageCompose()

    useEffect(() => {
        setFragmentMessage(undefined)
        setComments([])
    }, [topic.roomId])

    const handlerNewMessage = async (message: Communication.Message) => {
        if (message.roomId === topic.roomId) {
            setComments((prev) => [...prev, message])
            if (model.windowFocused) {
                document.getElementById("chatBody").scrollTop = document.getElementById("chatBody").scrollHeight + 100
            }
        }
    }

    useEffect(() => {
        const unSubscribeNewMessages = ChatWsV2.registerOnNewMessage(topic.roomId, (message) => {
            handlerNewMessage(message)
        })

        return () => {
            unSubscribeNewMessages()
        }
    }, [topic.roomId, handlerNewMessage])

    const hasMore = useMemo(() => {
        return !!fragmentMessage
    }, [fragmentMessage])

    const latestQuery = useQuery(
        ["room-latest", topic.roomId],
        () =>
            chatServiceV2.getLatestMessages({
                roomId: topic.roomId
            }),
        {
            onSuccess: (resp) => {
                const _comments = (resp.data || []).reverse()
                setComments(_comments)
                if (_comments.length > 0) {
                    const lastMessage = _comments[_comments.length - 1]
                    setFragmentMessage({
                        direction: "forward",
                        messageId: lastMessage.messageId
                    })
                    scrollToMessageId(lastMessage.messageId)
                }
            },
            onError: (err) => {
                handleErrorChat(err)
            },
            enabled: !!topic.roomId
        }
    )

    const getData = useCallback(
        async (page: number) => {
            try {
                if (
                    fetchingData.current &&
                    (fetchingData.current?.fetching ||
                        fetchingData.current?.fragmentMessage?.messageId === fragmentMessage.messageId)
                )
                    return

                fetchingData.current = {
                    fetching: true,
                    fragmentMessage
                }
                if (fragmentMessage) {
                    const {data} = await chatServiceV2.getFragmentMessages({
                        roomId: topic.roomId,
                        direction: fragmentMessage.direction,
                        messageId: fragmentMessage.messageId
                    })
                    const _comments = (data || []).reverse().filter((x) => x.messageId !== fragmentMessage.messageId)
                    setComments((prev) => [..._comments, ...prev])
                    if (_comments.length > 0) {
                        const firstMsg = _comments[0]
                        setFragmentMessage({
                            direction: "forward",
                            messageId: firstMsg.messageId
                        })
                    } else {
                        setFragmentMessage(undefined)
                    }
                }
            } catch (err) {
                handleErrorChat(err)
                setFragmentMessage(undefined)
            } finally {
                fetchingData.current = {
                    ...fetchingData.current,
                    fetching: false
                }
            }
        },
        [topic.roomId, fragmentMessage]
    )

    const onCommentSuccessfully = () => {
        latestQuery.refetch()
    }

    const onDeleteMessage = async (messageId: number) => {
        try {
            await chatServiceV2.deleteMessage({
                messageId: parseInt(messageId as unknown as string)
            })
            setComments((prev) => {
                const _comments = [...prev]
                const idx = _comments.findIndex((x) => x.messageId === messageId)
                if (idx >= 0) {
                    _comments.splice(idx, 1)
                }
                return _comments
            })
        } catch (e) {
            handleErrorChat(e)
        }
    }

    const scrollToMessageId = (messageId: number) => {
        setFoundMessageId(messageId)
        const el = document.getElementById(`message-${messageId}`)
        el && el.scrollIntoView({block: "start", behavior: "smooth"})
    }

    const handleEditComment = (_comment: Communication.Message) => {
        onEditMessage(_comment)
        composeRef.current.scrollIntoView({behavior: "smooth"})
    }

    const canEditComment = checkPermission({staff: [Permissions.Staff.Users.Students.Notes.Edit]}, model)
    const canDeleteComment = checkPermission({staff: [Permissions.Staff.Users.Students.Notes.Delete]}, model)

    const renderComments = () => {
        return (
            <>
                {comments.length > 0 && (
                    <div className={styles.commentContainer} id="chatBody">
                        <InfiniteScroll
                            pageStart={0}
                            loadMore={getData}
                            hasMore={hasMore}
                            initialLoad
                            useWindow={false}
                            isReverse={false}
                            loader={
                                <div key="loading-more" className={styles.loadMoreText}>
                                    Loading more...
                                </div>
                            }>
                            {comments.map((item, index) => {
                                return (
                                    <Message
                                        key={item.messageId}
                                        message={item}
                                        showOptions={true}
                                        containerStyle={styles.messageContainer}
                                        onClickReplyMessage={(_msg) => {
                                            scrollToMessageId(_msg.messageId)
                                        }}
                                        showTimestampSection
                                        prevMessageTime={index > 0 ? comments[index - 1].createdAt : undefined}
                                        // onEditMessage={canEditComment ? handleEditComment : undefined} //TODO: need BE support
                                        // onDeleteMessage={canDeleteComment ? onDeleteMessage : undefined} //TODO: need BE support
                                    />
                                )
                            })}
                        </InfiniteScroll>
                    </div>
                )}
                {(latestQuery.isLoading || latestQuery.isRefetching || fetchingData.current?.fetching) && <Spin />}
            </>
        )
    }

    const renderStacks = () => {
        const html: any = []
        const composeStack = (
            <div className={cx(styles.composeContainer, composeClassName)} ref={composeRef}>
                <Compose
                    editMessage={editMessage}
                    onlyContent
                    onSuccess={onCommentSuccessfully}
                    emojiPickerClassName={styles.emojiPicker}
                />
            </div>
        )

        const commentStack = renderComments()

        if (checkPermission({staff: [Permissions.Staff.Users.Students.Notes.Add]}, model)) {
            html.push(composeStack)
        }
        html.push(commentStack)
        html.reverse()

        return html
    }

    return (
        <div className={styles.container} id="thread-details">
            {renderStacks()}
        </div>
    )
}
